/**
 * LegaMenu — il nome della lega corrente, con la freccetta per cambiarla.
 *
 * Andrea 2026-09-14: su desktop sta a sinistra nell'intestazione, al
 * posto della scritta FantaSquad (il nome dell'app c'e' gia' nel logo);
 * nel menu mobile sta sotto il nome dell'utente connesso. Aprendolo si
 * vedono le leghe dell'utente (un tocco per passare) e il link per
 * crearne una nuova o entrare con un codice.
 *
 * Andrea 2026-10-10: «Gestisci» apre la gestione delle proprie leghe:
 * ordine (frecce), lega preferita (stella: si parte da li' aprendo l'app) e
 * «Lascia», visibile solo se si puo' lasciare la lega (nessuna squadra
 * presieduta, al massimo allenatore in seconda; non da presidente di lega).
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { type LegaMia, useCambiaLega, useGestioneLeghe, useLegheMie } from "../../hooks/useLeghe";
import { useAuthStore } from "../../lib/auth-store";
import { tr } from "../../i18n";

interface LegaMenuProps {
  variante: "desktop" | "mobile";
  /** Dopo un cambio lega o il link alle leghe (es. per chiudere il drawer). */
  onScelta?: () => void;
}

function errMsg(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err.response?.data?.detail ?? err.message ?? tr("Errore");
}

export function LegaMenu({ variante, onScelta }: LegaMenuProps) {
  const user = useAuthStore((s) => s.user);
  const [aperto, setAperto] = useState(false);
  const [gestisci, setGestisci] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  // Le leghe si caricano solo quando si apre il menu.
  const { data: leghe, isLoading } = useLegheMie(aperto);
  const cambia = useCambiaLega();
  const { ordina, preferita, abbandona } = useGestioneLeghe();
  const contenitore = useRef<HTMLDivElement>(null);

  // Desktop: si chiude cliccando fuori.
  useEffect(() => {
    if (!aperto || variante !== "desktop") return;
    const chiudi = (e: MouseEvent) => {
      if (contenitore.current && !contenitore.current.contains(e.target as Node)) {
        setAperto(false);
      }
    };
    document.addEventListener("mousedown", chiudi);
    return () => document.removeEventListener("mousedown", chiudi);
  }, [aperto, variante]);

  // Chiudendo il menu si esce anche dalla gestione.
  useEffect(() => {
    if (!aperto) {
      setGestisci(false);
      setErrore(null);
    }
  }, [aperto]);

  if (!user) return null;
  const nome = user.lega_corrente?.nome ?? tr("La mia lega");
  const correnteId = user.lega_corrente?.id ?? null;
  const elencoLeghe = leghe ?? [];
  const inCorso = ordina.isPending || preferita.isPending || abbandona.isPending;

  const sposta = (i: number, verso: -1 | 1) => {
    const j = i + verso;
    if (j < 0 || j >= elencoLeghe.length) return;
    const ids = elencoLeghe.map((l) => l.lega.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setErrore(null);
    ordina.mutate(ids, { onError: (e) => setErrore(errMsg(e)) });
  };

  const stella = (l: LegaMia) => {
    setErrore(null);
    preferita.mutate(
      { legaId: l.lega.id, preferita: !l.preferita },
      { onError: (e) => setErrore(errMsg(e)) },
    );
  };

  const esci = (l: LegaMia) => {
    if (
      !confirm(
        tr(
          "Lasciare la lega «{{v1}}»? Non la vedrai più nel menu; per rientrare servirà di nuovo il codice della lega o un invito.",
          { v1: l.lega.nome },
        ),
      )
    )
      return;
    setErrore(null);
    abbandona.mutate(l.lega.id, {
      onSuccess: () => {
        if (l.lega.id === correnteId) {
          setAperto(false);
          onScelta?.();
        }
      },
      onError: (e) => setErrore(errMsg(e)),
    });
  };

  const intestazione = (
    <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-primary-soft/30">
      <span className="text-[10px] uppercase tracking-wider text-fg-muted">
        {tr("Le tue leghe")}
      </span>
      {elencoLeghe.length > 0 && (
        <button
          type="button"
          onClick={() => {
            setGestisci((g) => !g);
            setErrore(null);
          }}
          className="text-[11px] font-bold text-accent hover:underline"
        >
          {gestisci ? tr("Fatto") : tr("Gestisci")}
        </button>
      )}
    </div>
  );

  const rigaNormale = (l: LegaMia) => {
    const attuale = l.lega.id === correnteId;
    return (
      <button
        key={l.lega.id}
        type="button"
        disabled={attuale}
        onClick={() => {
          setAperto(false);
          onScelta?.();
          if (!attuale) void cambia(l.lega.id);
        }}
        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 transition ${
          attuale ? "text-accent font-bold cursor-default" : "text-fg hover:bg-primary-soft/20"
        }`}
      >
        <span className="flex items-center gap-1.5 min-w-0">
          {l.preferita && (
            <span className="text-accent shrink-0" title={tr("Lega preferita")} aria-hidden>
              ★
            </span>
          )}
          <span className="truncate">{l.lega.nome}</span>
        </span>
        {attuale && <span className="text-xs shrink-0">✓</span>}
      </button>
    );
  };

  const rigaGestione = (l: LegaMia, i: number) => (
    <div key={l.lega.id} className="px-2 py-1.5 flex items-center gap-1.5 text-sm">
      <button
        type="button"
        disabled={inCorso}
        onClick={() => stella(l)}
        title={
          l.preferita
            ? tr("Togli dalle preferite")
            : tr("Segna come preferita: si apre questa quando entri nell'app")
        }
        aria-pressed={!!l.preferita}
        className={`w-7 h-7 shrink-0 rounded text-base leading-none transition ${
          l.preferita ? "text-accent" : "text-fg-muted hover:text-accent"
        }`}
      >
        {l.preferita ? "★" : "☆"}
      </button>
      <span
        className={`flex-1 min-w-0 truncate ${l.lega.id === correnteId ? "text-accent font-bold" : "text-fg"}`}
      >
        {l.lega.nome}
      </span>
      <div className="flex shrink-0">
        <button
          type="button"
          disabled={inCorso || i === 0}
          onClick={() => sposta(i, -1)}
          title={tr("Sposta su")}
          aria-label={tr("Sposta su")}
          className="w-7 h-7 rounded text-fg-muted hover:text-fg hover:bg-primary-soft/20 disabled:opacity-30"
        >
          ▲
        </button>
        <button
          type="button"
          disabled={inCorso || i === elencoLeghe.length - 1}
          onClick={() => sposta(i, 1)}
          title={tr("Sposta giù")}
          aria-label={tr("Sposta giù")}
          className="w-7 h-7 rounded text-fg-muted hover:text-fg hover:bg-primary-soft/20 disabled:opacity-30"
        >
          ▼
        </button>
      </div>
      {l.puo_abbandonare && (
        <button
          type="button"
          disabled={inCorso}
          onClick={() => esci(l)}
          title={tr("Lascia questa lega")}
          className="shrink-0 px-2 py-1 rounded border border-danger/40 text-danger text-[11px] font-bold hover:bg-danger/10 disabled:opacity-50"
        >
          {tr("Lascia")}
        </button>
      )}
    </div>
  );

  const elenco = (
    <div>
      {intestazione}
      {isLoading && <div className="px-3 py-2 text-xs text-fg-muted">{tr("Caricamento…")}</div>}
      {gestisci ? elencoLeghe.map(rigaGestione) : elencoLeghe.map(rigaNormale)}
      {gestisci && (
        <div className="px-3 py-2 text-[10px] leading-snug text-fg-muted border-t border-primary-soft/30">
          {tr(
            "★ = la lega che si apre quando entri nell'app. «Lascia» compare solo dove non presiedi una squadra (da allenatore in seconda puoi uscire).",
          )}
        </div>
      )}
      {errore && <div className="px-3 py-2 text-xs text-danger bg-danger/10">{errore}</div>}
      {!gestisci && (
        <Link
          to="/leghe"
          onClick={() => {
            setAperto(false);
            onScelta?.();
          }}
          className="block px-3 py-2 text-sm text-accent border-t border-primary-soft/30 hover:bg-primary-soft/20"
        >
          {tr("＋ Crea o entra in una lega")}
        </Link>
      )}
    </div>
  );

  if (variante === "desktop") {
    return (
      <div ref={contenitore} className="relative justify-self-start min-w-0">
        <button
          type="button"
          onClick={() => setAperto((a) => !a)}
          aria-expanded={aperto}
          aria-haspopup="menu"
          title={tr("Cambia lega")}
          className="flex flex-col items-start text-left min-w-0 max-w-[24rem] group"
        >
          <span className="text-[10px] uppercase tracking-[0.15em] text-fg-muted">
            {tr("Lega")}
          </span>
          <span className="flex items-center gap-2 min-w-0 font-display text-2xl tracking-wide text-accent group-hover:opacity-90">
            <span className="truncate">{nome}</span>
            <span
              className={`text-xl leading-none transition-transform ${aperto ? "rotate-180" : ""}`}
              aria-hidden
            >
              ▾
            </span>
          </span>
        </button>
        {aperto && (
          <div
            role="menu"
            className="absolute left-0 top-full mt-2 z-50 w-80 bg-bg-card border border-accent/30 rounded-card shadow-2xl overflow-hidden"
          >
            {elenco}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setAperto((a) => !a)}
        aria-expanded={aperto}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded bg-primary-deep/40 border border-primary-soft/30"
      >
        <span className="min-w-0 text-left">
          <span className="block text-[9px] uppercase tracking-wider text-fg-muted">
            {tr("Lega")}
          </span>
          <span className="block text-sm font-bold text-accent truncate">{nome}</span>
        </span>
        <span
          className={`text-sm leading-none text-accent transition-transform ${aperto ? "rotate-180" : ""}`}
          aria-hidden
        >
          ▾
        </span>
      </button>
      {aperto && (
        <div className="mt-1 rounded border border-primary-soft/30 overflow-hidden bg-bg-card">
          {elenco}
        </div>
      )}
    </div>
  );
}
